import * as THREE from 'three';
import { physicsWorld, getRapier } from './physics.js';
import { rigidBodies } from './objects-shared.js';
import { playerBody, playerYaw, playerPitch } from './player.js';
import { config } from './config.js';

let magnetActive = false;
let grabbedObject = null;
let grabStartTime = 0;
let holdDistance = 55;
let visualIndicator = null;

export function initMagnetGun(scene) {
    const indicatorGeo = new THREE.SphereGeometry(1, 16, 16);
    const indicatorMat = new THREE.MeshBasicMaterial({
        color: 0x00ffff,
        transparent: true,
        opacity: 0.4,
        wireframe: true
    });
    visualIndicator = new THREE.Mesh(indicatorGeo, indicatorMat);
    visualIndicator.visible = false;
    scene.add(visualIndicator);
}

export function startMagnetPull() {
    if (magnetActive || !playerBody || !config.magnet.enabled) return;
    
    const playerPos = playerBody.translation();
    const playerVec = new THREE.Vector3(playerPos.x, playerPos.y, playerPos.z);
    
    const lookDir = new THREE.Vector3(0, 0, -1);
    const euler = new THREE.Euler(playerPitch, playerYaw, 0, 'YXZ');
    lookDir.applyEuler(euler);
    
    let closestObj = null;
    let closestDist = Infinity;
    const maxDist = config.magnet.initialRadius;
    const coneAngle = Math.cos(config.magnet.coneAngle);
    
    for (const obj of rigidBodies) {
        if (!obj.userData.physicsBody) continue;
        
        const objPos = obj.userData.physicsBody.translation();
        const objVec = new THREE.Vector3(objPos.x, objPos.y, objPos.z);
        const toObj = objVec.clone().sub(playerVec);
        const dist = toObj.length();
        
        if (dist > maxDist) continue;
        
        toObj.normalize();
        const dotProduct = toObj.dot(lookDir);
        
        if (dotProduct > coneAngle && dist < closestDist) {
            closestDist = dist;
            closestObj = obj;
        }
    }
    
    if (closestObj) {
        magnetActive = true;
        grabbedObject = closestObj;
        grabStartTime = performance.now();
        holdDistance = Math.max(5, closestDist * 0.8);
        
        if (grabbedObject.userData.physicsBody) {
            grabbedObject.userData.physicsBody.setGravityScale(0.05);
        }
        
        if (visualIndicator) {
            visualIndicator.visible = true;
        }
    }
}

export function stopMagnetPull() {
    if (!magnetActive || !grabbedObject) return;
    
    if (grabbedObject.userData.physicsBody) {
        grabbedObject.userData.physicsBody.setGravityScale(1.0);
        
        const throwForce = config.magnet.throwForce;
        const lookDir = new THREE.Vector3(0, 0, -1);
        const euler = new THREE.Euler(playerPitch, playerYaw, 0, 'YXZ');
        lookDir.applyEuler(euler);
        
        const currentVel = grabbedObject.userData.physicsBody.linvel();
        
        grabbedObject.userData.physicsBody.setLinvel({
            x: lookDir.x * throwForce + currentVel.x * 0.3,
            y: lookDir.y * throwForce + currentVel.y * 0.3,
            z: lookDir.z * throwForce + currentVel.z * 0.3
        }, true);
        
        grabbedObject.userData.physicsBody.setAngvel({
            x: (Math.random() - 0.5) * 3,
            y: (Math.random() - 0.5) * 3,
            z: (Math.random() - 0.5) * 3
        }, true);
    }
    
    magnetActive = false;
    grabbedObject = null;
    
    if (visualIndicator) {
        visualIndicator.visible = false;
    }
}

export function updateMagnetGun(dt) {
    if (!magnetActive || !grabbedObject || !playerBody) return;
    
    const elapsed = (performance.now() - grabStartTime) / 1000;
    const playerPos = playerBody.translation();
    const playerVec = new THREE.Vector3(playerPos.x, playerPos.y + 0.6, playerPos.z);
    
    const lookDir = new THREE.Vector3(0, 0, -1);
    const euler = new THREE.Euler(playerPitch, playerYaw, 0, 'YXZ');
    lookDir.applyEuler(euler);
    
    const radiusGrowth = Math.min(elapsed * config.magnet.radiusGrowthRate, config.magnet.maxRadiusBonus);
    const currentRadius = config.magnet.initialRadius + radiusGrowth;
    
    const targetPos = playerVec.clone().add(lookDir.clone().multiplyScalar(holdDistance));
    
    const objBody = grabbedObject.userData.physicsBody;
    if (!objBody) {
        stopMagnetPull();
        return;
    }
    
    const objPos = objBody.translation();
    const objVec = new THREE.Vector3(objPos.x, objPos.y, objPos.z);
    
    const toTarget = targetPos.clone().sub(objVec);
    const dist = toTarget.length();
    
    const baseForce = config.magnet.pullForce;
    const distanceFactor = Math.max(0.3, 1 - (dist / currentRadius));
    const force = baseForce * distanceFactor;
    
    if (toTarget.length() > 0.01) {
        toTarget.normalize();
        
        objBody.applyImpulse({
            x: toTarget.x * force * dt * 60,
            y: toTarget.y * force * dt * 60,
            z: toTarget.z * force * dt * 60
        }, true);
    }
    
    const vel = objBody.linvel();
    const dampingFactor = 0.88;
    objBody.setLinvel({
        x: vel.x * dampingFactor,
        y: vel.y * dampingFactor,
        z: vel.z * dampingFactor
    }, true);
    
    const angVel = objBody.angvel();
    objBody.setAngvel({
        x: angVel.x * 0.92,
        y: angVel.y * 0.92,
        z: angVel.z * 0.92
    }, true);
    
    if (visualIndicator) {
        visualIndicator.position.copy(targetPos);
        visualIndicator.scale.setScalar(0.5 + Math.sin(elapsed * 5) * 0.2);
        visualIndicator.material.opacity = 0.3 + Math.sin(elapsed * 8) * 0.1;
    }
    
    if (dist > currentRadius * 2.5) {
        stopMagnetPull();
    }
}

export function isMagnetActive() {
    return magnetActive;
}

export function getGrabbedObject() {
    return grabbedObject;
}